import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Combobox,
  Divider,
  FormField,
  Heading,
  Label,
  Modal,
  Paragraph,
  Tag,
  TextButton,
  Toast,
  useToast,
} from '@zillow/constellation-10';
import { IconCameraExposureFilled } from '@zillow/constellation-icons';
import {
  useRentalManagerUserPropertySpotlightWebApiHandler_list,
  useRentalManagerUserPropertySpotlightWebApiHandler_reassign,
} from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

type Props = {
  isOpen: boolean;
  listingAlias: string;
  listingName: string;
  availabilityText: string;
  onClose: () => void;
  onSuccess: () => void;
};

const SpotlightReassignModal = ({ isOpen, listingAlias, listingName, availabilityText, onClose, onSuccess }: Props) => {
  const { enqueueToast } = useToast();
  const [fromListingAlias, setFromListingAlias] = useState<string>('');

  const { data: spotlightListings, isLoading: isLoadingSpotlightListings } =
    useRentalManagerUserPropertySpotlightWebApiHandler_list();
  const listings = spotlightListings?.listings ?? [];
  const spotlightListingsOptions = listings.map((listing) => ({
    label: listing.name,
    meta: listing.address,
    value: String(listing.listingAlias),
  }));

  useEffect(() => {
    const list = spotlightListings?.listings ?? [];
    if (!isLoadingSpotlightListings && list.length > 0 && !fromListingAlias) {
      setFromListingAlias(String(list[0].listingAlias));
    }
  }, [isLoadingSpotlightListings, spotlightListings, fromListingAlias]);
  const toastWithZillowTheme = (toast: React.ReactNode) => <div data-panda-theme="zillow">{toast}</div>;
  const { mutateAsync: reassignSpotlight, isPending } = useRentalManagerUserPropertySpotlightWebApiHandler_reassign({
    onSuccess: () => {
      enqueueToast(
        toastWithZillowTheme(<Toast tone="success">Listing Spotlight successfully reassigned to {listingName}.</Toast>),
      );
      onSuccess();
    },
    onError: () => {
      enqueueToast(
        toastWithZillowTheme(<Toast tone="critical">Listing Spotlight couldn’t be reassigned. Try again.</Toast>),
      );
      return;
    },
  });

  const handleReassign = async () => {
    await reassignSpotlight({
      getParams: { fromListingAlias, toListingAlias: listingAlias },
    });
  };

  const handleFromListingAliasChange = (value: string | Array<string>) => {
    setFromListingAlias(Array.isArray(value) ? (value[0] ?? '') : value);
  };
  return (
    <Modal.Root open={isOpen} onOpenChange={(open) => !open && onClose()} size={{ base: 'fullScreen', md: 'md' }}>
      <Modal.Portal>
        <Modal.Backdrop>
          <Modal.Content data-panda-theme="zillow">
            <Modal.Header>
              <Heading level={2} textStyle="heading-sm">
                Apply Listing Spotlight
              </Heading>
            </Modal.Header>
            <Divider />
            <Modal.Body>
              <Box css={{ display: 'flex', flexDirection: 'column', gap: '400', alignItems: 'flex-start' }}>
                <Tag size="md" tone="yellow" icon={<IconCameraExposureFilled />}>
                  {availabilityText}
                </Tag>
                <Paragraph>
                  You can still increase visibility for {listingName} by reassigning a Listing Spotlight from another
                  property.
                </Paragraph>
                <FormField
                  label={<Label required>Select a property</Label>}
                  required
                  css={{ alignSelf: 'stretch' }}
                  control={
                    <Combobox
                      value={fromListingAlias}
                      onChange={handleFromListingAliasChange}
                      showLabelForValue
                      options={spotlightListingsOptions}
                      placeholder="Select a property"
                      loading={isLoadingSpotlightListings}
                      size="md"
                    />
                  }
                />
                <Alert tone="info" css={{ alignSelf: 'stretch' }}>
                  Allow up to 24 hours for Listing Spotlight updates to apply across all platforms
                </Alert>
              </Box>
            </Modal.Body>
            <Divider />
            <Modal.Footer>
              <ButtonGroup aria-label="Actions">
                <TextButton tone="brand" onClick={onClose}>
                  Close
                </TextButton>
                <Button
                  tone="brand"
                  emphasis="filled"
                  size="md"
                  loading={isPending}
                  disabled={isPending || !fromListingAlias}
                  onClick={handleReassign}
                >
                  Reassign
                </Button>
              </ButtonGroup>
            </Modal.Footer>
            <Modal.CloseButton />
          </Modal.Content>
        </Modal.Backdrop>
      </Modal.Portal>
    </Modal.Root>
  );
};

export default SpotlightReassignModal;
